import React, { useState, useEffect } from 'react';

const CompanyModal = ({ isOpen, onClose, onSave, company = null, title }) => {
  const [formData, setFormData] = useState({
    org_name: '',
    org_type: '',
    industry: '',
    website: '',
    phone: '',
    address: '',
    license_type: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        org_name: company.detail?.org_name || '',
        org_type: company.detail?.org_type || '',
        industry: company.detail?.industry || '',
        website: company.detail?.website || '',
        phone: company.detail?.phone || '',
        address: company.detail?.address || '',
        license_type: company.detail?.license_type || ''
      });
    } else {
      setFormData({
        org_name: '',
        org_type: '',
        industry: '',
        website: '',
        phone: '',
        address: '',
        license_type: ''
      });
    }
    setErrors({});
  }, [company, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.org_name.trim()) {
      newErrors.org_name = 'Organization name is required';
    }
    
    if (!formData.org_type.trim()) {
      newErrors.org_type = 'Organization type is required';
    }
    
    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }
    
    if (formData.phone && !isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isValidPhone = (phone) => {
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving company:', error);
      setErrors({ submit: 'Failed to save company. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div 
        className="modal-backdrop show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal show d-block" 
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title || 'Company'}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {errors.submit && (
                  <div className="alert alert-danger" role="alert">
                    {errors.submit}
                  </div>
                )}
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Organization Name <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.org_name ? 'is-invalid' : ''}`}
                        name="org_name"
                        value={formData.org_name}
                        onChange={handleChange}
                        required
                      />
                      {errors.org_name && (
                        <div className="invalid-feedback">
                          {errors.org_name}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Organization Type <span className="text-danger">*</span>
                      </label>
                      <select 
                        className={`form-control ${errors.org_type ? 'is-invalid' : ''}`}
                        name="org_type"
                        value={formData.org_type}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select organization type...</option>
                        <option value="client">Client</option>
                        <option value="partner">Partner</option>
                        <option value="owner">Owner</option>
                      </select>
                      {errors.org_type && (
                        <div className="invalid-feedback">
                          {errors.org_type}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Industry</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Website</label>
                      <input 
                        type="url" 
                        className={`form-control ${errors.website ? 'is-invalid' : ''}`}
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        placeholder="https://example.com"
                      />
                      {errors.website && (
                        <div className="invalid-feedback">
                          {errors.website}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input 
                        type="tel" 
                        className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                      {errors.phone && (
                        <div className="invalid-feedback">
                          {errors.phone}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">License Type</label>
                      <select 
                        className="form-control"
                        name="license_type"
                        value={formData.license_type}
                        onChange={handleChange}
                      >
                        <option value="">Select license type...</option>
                        <option value="Full">Full</option>
                        <option value="POC">POC</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Company'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyModal;